import {
  AddAgentNote,
  AgentCommissionsQuery,
  AgentStatsSchema,
  AgentTreeSchema,
  AgentUserNoteSchema,
  AgentUserNoteType,
  ApiRoute,
  ApplicationRewardWithDetail,
  ApplicationRewardWithDetailSchema,
  ApplicationWithDetail,
  ApplicationWithDetailSchema,
  ListApplicationsQuery,
  ManagedCandidate,
  ManagedCandidateSchema,
  ManagedCandidatesQuery,
  PageData,
  PageDataSchema,
  getApiRoute
} from "@humanex/interfaces";
import { z } from "zod";

import { ApiClientConfig, BaseApiClient } from "./base-client";

export type { AgentStats } from "@humanex/interfaces";
export type { AgentTree } from "@humanex/interfaces";
export type { ManagedCandidate } from "@humanex/interfaces";
export type { ApplicationWithDetail } from "@humanex/interfaces";

export class AgentApiClient extends BaseApiClient {
  constructor(config: ApiClientConfig) {
    super({
      ...config,
      baseEndpoint: getApiRoute(ApiRoute.AGENT),
      requireAuth: true
    });
  }

  async getStats() {
    return this.get("stats", AgentStatsSchema);
  }

  async getTree() {
    return this.get("tree", AgentTreeSchema);
  }

  async getCandidates(params?: Partial<ManagedCandidatesQuery>): Promise<PageData<ManagedCandidate>> {
    const query = new URLSearchParams();
    if (params?.page != null) query.set("page", String(params.page));
    if (params?.limit != null) query.set("limit", String(params.limit));
    if (params?.search) query.set("search", params.search);
    const qs = query.toString();
    return this.get(qs ? `candidates?${qs}` : "candidates", PageDataSchema(ManagedCandidateSchema));
  }

  async getCommissions(
    params?: Partial<AgentCommissionsQuery>
  ): Promise<PageData<ApplicationRewardWithDetail>> {
    const query = new URLSearchParams();
    if (params?.page != null) query.set("page", String(params.page));
    if (params?.limit != null) query.set("limit", String(params.limit));
    if (params?.status) query.set("status", params.status);
    const qs = query.toString();
    return this.get(
      qs ? `commissions?${qs}` : "commissions",
      PageDataSchema(ApplicationRewardWithDetailSchema)
    );
  }

  async getCandidateNotes(accountId: string): Promise<AgentUserNoteType[]> {
    return this.get(`candidates/${accountId}/notes`, z.array(AgentUserNoteSchema));
  }

  async addCandidateNote(accountId: string, dto: AddAgentNote): Promise<AgentUserNoteType> {
    return this.post(`candidates/${accountId}/notes`, AgentUserNoteSchema, dto);
  }

  async getApplicationById(applicationId: string): Promise<ApplicationWithDetail> {
    return this.get(`applications/${applicationId}`, ApplicationWithDetailSchema);
  }

  async getCandidateApplications(
    userId: string,
    params?: Partial<ListApplicationsQuery>
  ): Promise<PageData<ApplicationWithDetail>> {
    const query = new URLSearchParams();
    if (params?.page != null) query.set("page", String(params.page));
    if (params?.limit != null) query.set("limit", String(params.limit));
    if (params?.status) query.set("status", params.status);
    const qs = query.toString();
    return this.get(
      qs ? `candidates/${userId}/applications?${qs}` : `candidates/${userId}/applications`,
      PageDataSchema(ApplicationWithDetailSchema)
    );
  }
}
